import React from 'react';

import getLegacyStepFromAnalysis, {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import ChooseDataFrameCreationMethodView from 'analysis-tool/data-frame-creation/ChooseDataFrameCreationMethodView';
import CombineDataFramesView from 'analysis-tool/data-frame-creation/combine-data-frames/CombineDataFramesView';
import CreateDataFrameFromAnalyticsBlockView from 'analysis-tool/data-frame-creation/CreateDataFrameFromAnalyticsBlockView';
import CreateDataFrameFromDataCatalogView from 'analysis-tool/data-frame-creation/data-catalog/CreateDataFrameFromDataCatalogView';
import CreateDataFrameFromLookUpView from 'analysis-tool/data-frame-creation/look-up/LookUpView';
import PlateSchemaDataFrameCreationView from 'analysis-tool/data-frame-creation/schemas/PlateSchemaDataFrameCreationView';
import RegistrySchemasDataFrameCreationView from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasDataFrameCreationView';
import CreateDataFrameFromUploadedCSVView from 'analysis-tool/data-frame-creation/upload-csv/CreateDataFrameFromUploadedCSVView';
import AnalysisDataLayoutAdapter from 'analysis-tool/data-layout/AnalysisDataLayoutAdapter';
import AnalysisDataViewAdapter from 'analysis-tool/data-view/AnalysisDataViewAdapter';
import AnalysisCreateOutputsView from 'analysis-tool/outputs/AnalysisCreateOutputsView';
import ExportDataCatalogDatasetOutputView from 'analysis-tool/outputs/ExportDataCatalogDatasetOutputView';
import SendToDocumentView from 'analysis-tool/outputs/SendToDocumentView';
import SaveToResultsStepConfigurationView from 'analysis-tool/structured-data-mapping/SaveToResultsStepConfigurationView';
import SaveToResultsStepOutputView from 'analysis-tool/structured-data-mapping/SaveToResultsStepOutputView';
import AnalysisTemplateApplyView from 'analysis-tool/templates/AnalysisTemplateApplyView';
import AnalysisTemplateConfigurationView from 'analysis-tool/templates/AnalysisTemplateConfigurationView';
import AnalysisComponentErrorBoundary from 'analysis-tool/ui/components/AnalysisComponentErrorBoundary';
import AnalysisFlowchartView from 'analysis-tool/ui/flowchart-view/AnalysisFlowchartView';
import useLogModalSupportedStepProfilerTimings from 'analysis-tool/ui/hooks/useLogModalSupportedStepProfilerTimings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import EditRegressionView from './EditRegressionView';
import EmptyAnalysisView from './EmptyAnalysisView';
import FailedStepView from './FailedStepView';
import HaltedStepView from './HaltedStepView';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  MainViewComponentProps,
  StepAwareAnalysisMainViewItemType,
} from './MainViewTypes';
import RegressionView from './RegressionView';
import RunningStepView from './RunningStepView';

const ITEM_TYPE_TO_VIEW_COMPONENT: {
  [key in AnalysisMainViewItemType]: React.FC<MainViewComponentProps<key>>;
} = {
  [AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD]: ChooseDataFrameCreationMethodView,
  [AnalysisMainViewItemType.COMBINE_DATA_FRAMES]: CombineDataFramesView,
  [AnalysisMainViewItemType.CREATE_OUTPUTS]: AnalysisCreateOutputsView,
  [AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION]: CreateDataFrameFromDataCatalogView,
  [AnalysisMainViewItemType.DATA_LAYOUT]: AnalysisDataLayoutAdapter,
  [AnalysisMainViewItemType.DATA_VIEW]: AnalysisDataViewAdapter,
  [AnalysisMainViewItemType.EMPTY_ANALYSIS]: EmptyAnalysisView,
  [AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT]: ExportDataCatalogDatasetOutputView,
  [AnalysisMainViewItemType.FAILED]: FailedStepView,
  [AnalysisMainViewItemType.HALTED]: HaltedStepView,
  [AnalysisMainViewItemType.FLOWCHART]: AnalysisFlowchartView,
  [AnalysisMainViewItemType.INSIGHTS_BLOCK_DATASET_CONFIGURATION]: CreateDataFrameFromAnalyticsBlockView,
  [AnalysisMainViewItemType.LOOK_UP]: CreateDataFrameFromLookUpView,
  [AnalysisMainViewItemType.REGISTRY_SCHEMA_DATASET_CONFIGURATION]: RegistrySchemasDataFrameCreationView,
  [AnalysisMainViewItemType.PLATE_SCHEMA_DATASET_CONFIGURATION]: PlateSchemaDataFrameCreationView,
  [AnalysisMainViewItemType.UPLOAD_CSV_DATASET_CONFIGURATION]: CreateDataFrameFromUploadedCSVView,
  [AnalysisMainViewItemType.RUNNING]: RunningStepView,
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_CONFIGURATION]: SaveToResultsStepConfigurationView,
  [AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT]: SaveToResultsStepOutputView,
  [AnalysisMainViewItemType.SEND_TO_DOCUMENT]: SendToDocumentView,
  [AnalysisMainViewItemType.TEMPLATE_APPLY]: AnalysisTemplateApplyView,
  [AnalysisMainViewItemType.TEMPLATE_CONFIGURATION]: AnalysisTemplateConfigurationView,
  [AnalysisMainViewItemType.REGRESSION]: RegressionView,
  [AnalysisMainViewItemType.EDIT_REGRESSION]: EditRegressionView,
};

// This function is expected to return a clientStateId that will be unique for a given instance of a MainView
// component within a single Analysis; hence e.g. appending the `stepId` for step-aware components (which,
// in the future, may have multiple instances within an Analysis)
function getClientStateIdForMainView(
  clientStateIdForAnalysis: string,
  selectedItem: AnalysisMainViewItem
): string {
  if (isStepAwareAnalysisItem(selectedItem)) {
    return `${clientStateIdForAnalysis}-${selectedItem.stepId}`;
  } else {
    return clientStateIdForAnalysis;
  }
}

// The AnalysisUIMainViewRouter handles mapping from (selectedItem + analysis) -> step when necessary.
export type AnalysisUIMainViewRouterProps = Omit<
  MainViewComponentProps<AnalysisMainViewItemType>,
  'step' | 'writable'
>;

/** Router for mapping (analysis + selectedItem + shared props) to a component to render in the AnalysisUI
 *  main view.
 *
 * The router allows for two different types of "main view" components:
 * - Step-agnostic views. These are views that don't have a related Pipeline step. For example,
 *   EmptyAnalysisView does not have a related step and therefore should not be passed a step prop.
 * - Step-aware views. These are views that do have a related Pipeline step. For example, RegressionView
 *   needs to be aware of its related REGRESSION pipeline step, and so is passed the relevant GraphQL step
 *   object.
 *
 * The choice to use steps as an important piece of data is intentional for a few reasons:
 * - It improves testability and composability to pass only as much information as is needed. We pass in the
 *   analysis to accommodate instances in which a step needs to other steps in the analysis.
 * - This works towards having sidebar items, header breadcrumbs, and main view items all being derived from
 *   the selected AnalysisMainViewItem.
 * - We want to have consistency in the props passed to all main views (like a Plugin system), rather than
 *   handling each possible type -> component + props mapping explicitly and separately. This has advantages
 *   (e.g. making it easier to add new main views) and disadvantages (e.g. main views receive more props
 *   than they necessarily need).
 */
export default function AnalysisUIMainViewRouter({
  analysis,
  clientStateId,
  selectedItem,
  ...restProps
}: AnalysisUIMainViewRouterProps): JSX.Element {
  const mainViewClientStateId = getClientStateIdForMainView(clientStateId, selectedItem);
  useLogModalSupportedStepProfilerTimings(analysis);

  if ('isUpdating' in selectedItem && selectedItem.isUpdating) {
    return <CenteredHorizontalThrobber />;
  }

  if (isStepAwareAnalysisItem(selectedItem)) {
    const ViewComponent: React.FC<MainViewComponentProps<StepAwareAnalysisMainViewItemType>> =
      ITEM_TYPE_TO_VIEW_COMPONENT[selectedItem.type];
    if (getNullableStepFromAnalysis(analysis, selectedItem.stepId) == null) {
      return <CenteredHorizontalThrobber />;
    }
    const step = getLegacyStepFromAnalysis(analysis, selectedItem.stepId);
    return (
      <AnalysisComponentErrorBoundary
        errorExtras={{
          analysisId: analysis.id,
          selectedItem,
          stepId: step.id,
          stepType: step.definition.stepType,
        }}
      >
        <ViewComponent
          {...restProps}
          analysis={analysis}
          analysisClientStateId={clientStateId}
          clientStateId={mainViewClientStateId}
          selectedItem={selectedItem}
          step={step}
        />
      </AnalysisComponentErrorBoundary>
    );
  } else {
    const ViewComponent: React.FC<
      MainViewComponentProps<Exclude<AnalysisMainViewItemType, StepAwareAnalysisMainViewItemType>>
    > = ITEM_TYPE_TO_VIEW_COMPONENT[selectedItem.type];
    return (
      <AnalysisComponentErrorBoundary errorExtras={{analysisId: analysis.id, selectedItem}}>
        <ViewComponent
          {...restProps}
          analysis={analysis}
          clientStateId={mainViewClientStateId}
          selectedItem={selectedItem}
        />
      </AnalysisComponentErrorBoundary>
    );
  }
}
